import { type Rocket, TICK_RATE, type World } from '../../../sim/types';
import type { PixelCamera } from '../../camera/PixelCamera';
import { IDENTITY, lerp, put, yawQuat } from './batch';
import { type Fx, ROCKET_HEIGHT } from './context';

/*
 * Mirrors of the world's live projectiles, rewritten from the sim state every frame so
 * there is nothing to keep in step: bullets as tracers, thrown grenades, rockets with a
 * smoke trail, and placed bombs with a lamp that blinks faster as the fuse runs down.
 */

const TRACER_HEIGHT = 1.1;
/** One smoke puff per this many metres of rocket flight. */
const TRAIL_SPACING = 0.5;
/** Most puffs one rocket may drop in a frame. */
const TRAIL_BURST = 4;

interface Trail {
  /** `Rocket.travelled` at the last puff dropped. */
  emitted: number;
  frame: number;
}

export class Projectiles {
  private readonly trails = new Map<number, Trail>();
  private time = 0;
  private frame = 0;

  constructor(private readonly fx: Fx) {}

  /** `alpha` interpolates between the previous and current tick where the contract gives both. */
  mirror(world: World, dt: number, alpha: number, camera: PixelCamera): void {
    this.time += dt;
    this.frame++;
    this.bullets(world, alpha, camera);
    this.thrown(world, camera);
    this.rockets(world, alpha, camera);
    this.bombs(world);
  }

  clear(): void {
    this.trails.clear();
  }

  private bullets(world: World, alpha: number, camera: PixelCamera): void {
    const { b } = this.fx.set;
    for (const bullet of world.bullets) {
      const x = lerp(bullet.prevX, bullet.x, alpha);
      const z = lerp(bullet.prevZ, bullet.z, alpha);
      const batch = bullet.fromPlayer ? b.tracerPlayer : b.tracerEnemy;
      put(batch, camera, x, this.fx.groundY(x, z) + TRACER_HEIGHT, z, 1, 1, 1, yawQuat(Math.atan2(bullet.dirX, bullet.dirZ)));
    }
  }

  /** `Thrown` has no previous position, so grenades are drawn where the last tick left them. */
  private thrown(world: World, camera: PixelCamera): void {
    for (const thrown of world.thrown) {
      put(this.fx.set.b.grenade, camera, thrown.x, this.fx.groundY(thrown.x, thrown.z) + thrown.y, thrown.z, 1, 1, 1, IDENTITY);
    }
  }

  private rockets(world: World, alpha: number, camera: PixelCamera): void {
    // The flame gutters on twos.
    const flame = Math.floor(this.time * 24) % 2 === 0 ? 1 : 0.65;
    for (const rocket of world.rockets) this.rocket(rocket, rocket.id, alpha, camera, flame);
    // A rocket trooper's rocket is the same thing coming the other way; its ids are a series of their own.
    const incoming = world.hazards.enemyRockets;
    for (const rocket of incoming) this.rocket(rocket, -rocket.id - 1, alpha, camera, flame);
    if (this.trails.size > world.rockets.length + incoming.length) {
      for (const [id, trail] of this.trails) if (trail.frame !== this.frame) this.trails.delete(id);
    }
  }

  private rocket(rocket: Rocket, key: number, alpha: number, camera: PixelCamera, flame: number): void {
    const { rng, particles, set } = this.fx;
    const x = lerp(rocket.prevX, rocket.x, alpha);
    const z = lerp(rocket.prevZ, rocket.z, alpha);
    const y = this.fx.groundY(x, z) + ROCKET_HEIGHT;
    const q = yawQuat(Math.atan2(rocket.dirX, rocket.dirZ));
    put(set.b.rocketBody, camera, x, y, z, 1, 1, 1, q);
    put(set.b.rocketTail, camera, x, y, z, 1, 1, flame, q);

    const travelled = rocket.travelled - (1 - alpha) * Math.hypot(rocket.x - rocket.prevX, rocket.z - rocket.prevZ);
    let trail = this.trails.get(key);
    if (!trail) {
      trail = { emitted: travelled, frame: this.frame };
      this.trails.set(key, trail);
    }
    trail.frame = this.frame;
    let dropped = 0;
    while (trail.emitted + TRAIL_SPACING <= travelled && dropped < TRAIL_BURST) {
      trail.emitted += TRAIL_SPACING;
      const back = travelled - trail.emitted + 0.7;
      particles.puff(rng.pick(set.smoke), x - rocket.dirX * back, y, z - rocket.dirZ * back, rng.range(0.2, 0.32), rng.range(0.45, 0.7), 0.5, 0);
      dropped++;
    }
    // A long frame would owe a backlog of puffs: forget it rather than burst them all at once.
    if (dropped === TRAIL_BURST) trail.emitted = Math.max(trail.emitted, travelled - TRAIL_SPACING);
  }

  private bombs(world: World): void {
    const { b } = this.fx.set;
    for (const bomb of world.bombs) {
      const y = this.fx.groundY(bomb.x, bomb.z);
      put(b.bombBody, null, bomb.x, y, bomb.z, 1, 1, 1, IDENTITY);
      // The lamp is shown or hidden, never dimmed; it blinks on the sim's own tick so audio can match.
      const seconds = bomb.fuseTicks / TICK_RATE;
      const period = seconds > 10 ? 60 : seconds > 5 ? 30 : seconds > 2 ? 16 : 8;
      if (bomb.fuseTicks % period < period / 2) put(b.bombLamp, null, bomb.x, y, bomb.z, 1, 1, 1, IDENTITY);
    }
  }
}
